/**
 * A gaveta de configuração — tudo que era painel fixo, atrás de uma engrenagem.
 *
 * ## Por que sair da tela principal
 *
 * O painel ocupava uma coluna inteira, permanentemente, para oferecer escolhas
 * que se fazem uma vez por semana: modo de voz, bancada de vozes, diagnóstico.
 * Configuração é trabalho raro ocupando espaço nobre; a conversa é o trabalho
 * de todo dia e estava espremida no meio.
 *
 * ## O que uma gaveta precisa ter para não ser uma armadilha
 *
 * Três coisas, e nenhuma é enfeite:
 *
 *  · **Escape fecha.** É o primeiro reflexo de quem abriu sem querer.
 *  · **Clicar fora fecha.** É o segundo.
 *  · **O foco entra e volta.** Abrir e deixar o foco no fundo faz o teclado
 *    navegar uma tela que a pessoa não está vendo; fechar sem devolver o foco
 *    joga quem usa teclado para o começo do documento.
 *
 * O `inert` no fundo seria a solução completa e ainda não é universal; enquanto
 * isso, o `aria-hidden` na casca e o foco trazido para dentro resolvem o caso
 * real, que é ler a tela errada com leitor de tela.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export function Gaveta({
  aberta,
  onFechar,
  titulo,
  children,
}: {
  aberta: boolean;
  onFechar: () => void;
  titulo: string;
  children: ReactNode;
}): JSX.Element | null {
  const caixa = useRef<HTMLDivElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!aberta) return undefined;

    focoAnterior.current = document.activeElement as HTMLElement | null;
    // O primeiro foco vai para a própria caixa, e não para o primeiro botão:
    // começar num controle faz o leitor de tela anunciar a ação antes do
    // título, e a pessoa não sabe onde entrou.
    caixa.current?.focus();

    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onFechar();
      }
    };
    document.addEventListener('keydown', aoTeclar);

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      focoAnterior.current?.focus();
    };
  }, [aberta, onFechar]);

  if (!aberta) return null;

  return (
    <div className="u1-gaveta" role="presentation">
      {/* O véu é botão de verdade, e não uma div com onClick: assim ele aparece
          na navegação por teclado como "fechar", em vez de ser uma área morta
          que só o mouse conhece. */}
      <button type="button" className="u1-gaveta__veu" onClick={onFechar}>
        <span className="u1-oculto">Fechar configuração</span>
      </button>

      <div
        className="u1-gaveta__caixa"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        ref={caixa}
      >
        <header className="u1-gaveta__topo">
          <h2 className="u1-gaveta__titulo">{titulo}</h2>
          <button type="button" className="u1-gaveta__fechar" onClick={onFechar}>
            <span aria-hidden="true">×</span>
            <span className="u1-oculto">Fechar</span>
          </button>
        </header>

        <div className="u1-gaveta__corpo">{children}</div>
      </div>
    </div>
  );
}

/**
 * A engrenagem. Desenhada em SVG e não em texto: o caractere de engrenagem
 * varia de forma e de tamanho entre sistemas, e um ícone que muda de peso
 * conforme a máquina não é ícone, é sorte.
 */
export function Engrenagem(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19.14 12.94a7.6 7.6 0 0 0 .05-.94 7.6 7.6 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.62l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.67 8.86a.5.5 0 0 0 .12.62l2.03 1.58c-.03.31-.05.63-.05.94s.02.63.05.94l-2.03 1.58a.5.5 0 0 0-.12.62l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.25.42.5.42h3.84c.25 0 .46-.18.5-.42l.36-2.54c.59-.24 1.12-.56 1.62-.94l2.39.96c.22.08.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.62l-2.03-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z"
      />
    </svg>
  );
}
